import {
  CommandPaletteOption,
  CompanyEntry,
  CompanyGroupEntry,
  PaymentEntry,
  PositionEntry,
  RouteEntry,
  ShiftEntry,
  UserEntry,
  WorkItemEntry,
} from './CommandPalette_Utils'

export const HISTORY_REFERENCE_KEY = '__historyReference' as const

const HISTORY_STORAGE_KEYS = {
  USERS: 'commandPalette_history_users',
  COMPANIES: 'commandPalette_history_companies',
  COMPANY_GROUPS: 'commandPalette_history_company_groups',
  ROUTES: 'commandPalette_history_routes',
  SHIFTS: 'commandPalette_history_shifts',
  WORK_ITEMS: 'commandPalette_history_work_items',
  PAYMENTS: 'commandPalette_history_payments',
  POSITIONS: 'commandPalette_history_positions',
} as const

const HISTORY_LIMITS = {
  USERS: 6,
  COMPANIES: 6,
  COMPANY_GROUPS: 6,
  ROUTES: 2,
  SHIFTS: 3,
  WORK_ITEMS: 3,
  PAYMENTS: 3,
  POSITIONS: 3,
} as const

export type HistoryType = CommandPaletteOption['historyType']

export type HistoryByType = {
  users: UserEntry[]
  companies: CompanyEntry[]
  company_groups: CompanyGroupEntry[]
  routes: RouteEntry[]
  shifts: ShiftEntry[]
  work_items: WorkItemEntry[]
  payments: PaymentEntry[]
  positions: PositionEntry[]
}

function getStorageKey(type: HistoryType): string {
  switch (type) {
    case 'users':
      return HISTORY_STORAGE_KEYS.USERS
    case 'companies':
      return HISTORY_STORAGE_KEYS.COMPANIES
    case 'company_groups':
      return HISTORY_STORAGE_KEYS.COMPANY_GROUPS
    case 'routes':
      return HISTORY_STORAGE_KEYS.ROUTES
    case 'shifts':
      return HISTORY_STORAGE_KEYS.SHIFTS
    case 'work_items':
      return HISTORY_STORAGE_KEYS.WORK_ITEMS
    case 'payments':
      return HISTORY_STORAGE_KEYS.PAYMENTS
    case 'positions':
      return HISTORY_STORAGE_KEYS.POSITIONS
  }
}

function getHistoryLimit(type: HistoryType): number {
  switch (type) {
    case 'users':
      return HISTORY_LIMITS.USERS
    case 'companies':
      return HISTORY_LIMITS.COMPANIES
    case 'company_groups':
      return HISTORY_LIMITS.COMPANY_GROUPS
    case 'routes':
      return HISTORY_LIMITS.ROUTES
    case 'shifts':
      return HISTORY_LIMITS.SHIFTS
    case 'work_items':
      return HISTORY_LIMITS.WORK_ITEMS
    case 'payments':
      return HISTORY_LIMITS.PAYMENTS
    case 'positions':
      return HISTORY_LIMITS.POSITIONS
  }
}

function readHistoryFromStorage(type: HistoryType): CommandPaletteOption[] {
  const storageKey = getStorageKey(type)
  try {
    const stored = localStorage.getItem(storageKey)
    if (!stored) return []
    const parsed = JSON.parse(stored)
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

export function getHistoryType(item: CommandPaletteOption): HistoryType {
  return item.historyType as HistoryType
}

// Store only the minimum fields for each item
function toHistoryReference(item: CommandPaletteOption): CommandPaletteOption {
  const historyType = getHistoryType(item)
  switch (historyType) {
    case 'users': {
      const u = item as UserEntry
      return {
        historyType,
        id: u.id,
        name: u.name,
        email: u.email,
      }
    }
    case 'companies': {
      const c = item as CompanyEntry
      return {
        historyType,
        id: c.id,
        name: c.name,
        type: c.type,
      }
    }
    case 'company_groups': {
      const g = item as CompanyGroupEntry
      return {
        ...g,
        historyType,
      }
    }
    case 'shifts': {
      const s = item as ShiftEntry
      return {
        historyType,
        id: s.id,
        startsAt: s.startsAt,
      }
    }
    case 'work_items': {
      const w = item as WorkItemEntry
      return {
        historyType,
        id: w.id,
        workerId: w.workerId,
        shiftId: w.shiftId,
        status: w.status,
        startedAt: w.startedAt,
        completedAt: w.completedAt,
      }
    }
    case 'payments': {
      const p = item as PaymentEntry
      return {
        historyType,
        id: p.id,
        amount: p.amount,
        status: p.status,
      }
    }
    case 'positions': {
      const p = item as PositionEntry
      return {
        historyType,
        id: p.id,
        name: p.name,
        companyName: p.companyName,
      }
    }
    case 'routes':
    default: {
      const r = item as RouteEntry
      return {
        historyType,
        id: r.id,
        path: r.path,
        label: r.label,
      }
    }
  }
}

export function addToHistory(item: CommandPaletteOption): void {
  const historyType = getHistoryType(item)
  if (!historyType || !('historyType' in item)) return

  const storageKey = getStorageKey(historyType)
  const limit = getHistoryLimit(historyType)

  try {
    const existingHistory = readHistoryFromStorage(historyType)
    const historyReference = toHistoryReference(item)

    // remove the item if it already exists to avoid duplicates
    const filteredHistory = existingHistory.filter(
      (existingItem) => String(existingItem.id) !== String(historyReference.id)
    )

    // Add the new item to the front
    const newHistory = [historyReference, ...filteredHistory].slice(0, limit)

    localStorage.setItem(storageKey, JSON.stringify(newHistory))
  } catch (error) {
    console.error('Failed to save to history:', error)
  }
}

export function getHistory(type: HistoryType): CommandPaletteOption[] {
  return readHistoryFromStorage(type).map((item) => {
    // Migrate old route items that used `key` to `id`
    const base =
      type === 'routes' && 'key' in item && !('id' in item)
        ? Object.assign({}, item, { id: (item as { key: string }).key })
        : item
    return Object.assign({}, base, {
      [HISTORY_REFERENCE_KEY]: true,
    }) as CommandPaletteOption
  })
}

/** Returns history grouped by type with correct entry types. Empty arrays when hasSearch. */
export function getHistoryByType(hasSearch: boolean): HistoryByType {
  const empty: HistoryByType = {
    users: [],
    companies: [],
    company_groups: [],
    routes: [],
    shifts: [],
    work_items: [],
    payments: [],
    positions: [],
  }
  if (hasSearch) return empty
  return {
    users: getHistory('users') as UserEntry[],
    companies: getHistory('companies') as CompanyEntry[],
    company_groups: getHistory('company_groups') as CompanyGroupEntry[],
    routes: getHistory('routes') as RouteEntry[],
    shifts: getHistory('shifts') as ShiftEntry[],
    work_items: getHistory('work_items') as WorkItemEntry[],
    payments: getHistory('payments') as PaymentEntry[],
    positions: getHistory('positions') as PositionEntry[],
  }
}

export function clearHistory(type?: HistoryType): void {
  if (type) {
    const storageKey = getStorageKey(type)
    localStorage.removeItem(storageKey)
  } else {
    // Clear all history
    Object.values(HISTORY_STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key)
    })
  }
}
